import dayjs, { Dayjs } from "dayjs";

import { DateTimePickerInput } from "../../../components/DateTimePicker";

/** What the value is stored and handed back as — `<input type="date">`'s. */
const DATE_VALUE = "YYYY-MM-DD";
/** `<input type="datetime-local">`'s. */
const DATE_TIME_VALUE = "YYYY-MM-DDTHH:mm";

interface IAdminDateFieldProps {
  /** Ties the field to its `FilterLabel`. */
  id: string;
  /** `YYYY-MM-DD`, or `YYYY-MM-DDTHH:mm` when `withTime`. Blank for unset. */
  value: string;
  onChange: (value: string) => void;
  withTime?: boolean;
  disabled?: boolean;
}

/**
 * The date field for the /admin pages (EN-5146 UI feedback).
 *
 * These were `<input type="date">` and `<input type="datetime-local">`, which
 * open Chrome's own picker: a blue-accented panel rendered outside the page
 * that no stylesheet can reach — `accent-color` brands the control itself but
 * not the popup. It read as an unstyled browser widget dropped into the
 * portal. This wraps the portal's themed picker instead.
 *
 * It reads and writes the same strings the native inputs did, so a caller
 * only swaps the element: `dayjs` parses and formats both shapes in local
 * time, which is what `datetime-local` meant by them.
 */
export function AdminDateField({
  id,
  value,
  onChange,
  withTime = false,
  disabled = false,
}: IAdminDateFieldProps) {
  const valueFormat = withTime ? DATE_TIME_VALUE : DATE_VALUE;
  const parsed = value ? dayjs(value) : null;

  return (
    <DateTimePickerInput
      id={id}
      width="100%"
      showTime={withTime}
      format={withTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY"}
      placeholder={withTime ? "Select date & time" : "Select a date"}
      disabled={disabled}
      value={parsed?.isValid() ? parsed : null}
      // Clearing hands back null despite the signature, hence the guard.
      onChange={(next: Dayjs | null) =>
        onChange(next?.isValid() ? next.format(valueFormat) : "")
      }
    />
  );
}
